<script lang="ts" setup>import { computed as _computed } from 'vue';
const explicitViewBox = __MACROS_toRef(__props, "viewBox");
const providedSize = __MACROS_toRef(__props, "size");
const explicitIcon = __MACROS_toRef(__props, "icon");
const deprecatedName = __MACROS_toRef(__props, "name");

import { toRef as __MACROS_toRef } from "vue";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import type { IconDefinition as DuotoneIconDefinition } from '@fortawesome/duotone-regular-svg-icons'
import { notifyError } from '@shared/errors'

export type GIconDefinition = IconDefinition | DuotoneIconDefinition | undefined

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

withDefaults(defineProps<{
  name?: string
  icon?: GIconDefinition | string
  /**
  * SVG markup, imported directly: `import icon from '~/…/icon.svg?svgicon'`.
  * Renders the SVG inline — statically analyzable and tree-shaken, with no
  * runtime lookup. The preferred way to render a custom (non-FontAwesome) icon.
  * `?svgicon` processes the file exactly as the old sprite did, so it renders
  * identically; use `?raw` for the untouched source. For an already-imported
  * icon carried through a wrapper's `icon` prop, pass it to `icon` instead.
  **/
  svg?: string
  prefix?: string
  color?: string
  size?: string | IconSize
  /**
  * expects a css variable name, e.g. `--gradient-color`
  **/
  gradientColorName?: string
  viewBox?: string
}>(), { size: 'sm', })

const isSvgMarkup = (value: unknown): value is string =>
  typeof value === 'string' && value.startsWith('<svg')

const svgMarkup = _computed(() => __props.svg ?? (isSvgMarkup(__props.icon) ? __props.icon : undefined))
const icon = _computed(() => svgMarkup.value ? undefined : __props.icon as IconDefinition)

// Render the `svg` prop as the root `<svg class="g-icon">` itself (not wrapped),
// so the output matches every other icon. We lift the source's `viewBox` onto
// that root, keep its remaining root attributes (e.g. `fill="none"`) on an inner
// `<g>` — mirroring the sprite `<symbol>` boundary so `.g-icon`'s currentColor
// fill/stroke don't override the icon's own defaults — and inline the rest.
const parsedSvg = _computed(() => {
  if (!svgMarkup.value) return undefined

  const open = svgMarkup.value.match(/<svg\b([^>]*)>/i)?.[1] ?? ''
  const inner = svgMarkup.value
    .replace(/^[\s\S]*?<svg\b[^>]*>/i, '')
    .replace(/<\/svg\s*>\s*$/i, '')

  const rootAttrs: Record<string, string> = {}
  for (const [, key, value] of open.matchAll(/([\w:-]+)\s*=\s*"([^"]*)"/g))
    rootAttrs[key] = value

  const { viewBox, preserveAspectRatio } = rootAttrs
  const { class: rootClass, width, height, xmlns, viewBox: _vb, preserveAspectRatio: _par, ...groupAttrs } = rootAttrs

  // The `?svgicon` plugin tags multicolor illustrations with `g-illustration`
  // on the root `<svg>`; lift it onto our `.g-icon` so its fills/strokes win.
  const illustration = /\bg-illustration\b/.test(rootClass ?? '')

  return {
    viewBox: __props.viewBox || viewBox,
    preserveAspectRatio,
    groupAttrs,
    inner,
    illustration,
  }
})

const originalSize = _computed(() => {
  switch (__props.size) {
    case '30px': return 'xl'
    case '24px': return 'lg'
    case '20px': return 'md'
    case '16px': return 'sm'
    case '12px': return 'xs'
  }
  return __props.size
})

const dimension = _computed(() => {
  if (originalSize.value?.endsWith('px') || originalSize.value?.endsWith('rem') || originalSize.value?.endsWith('em'))
    return originalSize.value
})

const sizeName = _computed(() => {
  if (!dimension.value) return originalSize.value
})

const attrs = _computed(() => {
  return {
    class: [
      sizeName.value && `g-icon-${sizeName.value}`,
      parsedSvg.value?.illustration && 'g-illustration',
      __props.color && !__props.color.startsWith('#') && `text-${__props.color}`,
    ],
    style: [
      __props.color?.startsWith('#') && { color: __props.color },
      dimension.value && { height: dimension.value, width: dimension.value },
    ],
  }
})

const gradientMaskUrl = _computed(() => {
  // Only calculate if we have both an icon object and a gradient
  if (!icon.value || !__props.gradientColorName) return ''

  const iconData = icon.value.icon
  if (!iconData) return ''

  // FontAwesome icon array structure: [width, height, ligatures, unicode, svgPathData]
  const width = iconData[0]
  const height = iconData[1]
  const pathData = iconData[4]

  let pathHtml = ''
  // Standard icons return a string. Duotone icons return an array of strings.
  if (typeof pathData === 'string') {
    pathHtml = `<path d="${pathData}" fill="currentColor" />`
  }
  else if (Array.isArray(pathData)) {
    pathHtml = pathData.map(p => `<path d="${p}" fill="currentColor" />`).join('')
  }

  // Build the raw SVG and safely encode it for the CSS url()
  const rawSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">${pathHtml}</svg>`
  return `url('data:image/svg+xml;utf8,${encodeURIComponent(rawSvg)}')`
})

onMounted(() => {
  const el = getCurrentInstance()?.vnode?.el as Element | undefined

  if (__props.name || (typeof __props.icon === 'string' && !isSvgMarkup(__props.icon))) {
    notifyError(new Error(`GIcon received an unresolvable icon name: "${__props.icon}"`), {
      tags: { component: 'GIcon' },
      extra: {
        icon: __props.name || __props.icon,
        location: el?.parentElement?.outerHTML?.slice(0, 300),
      },
    })
  }

  if (import.meta.env.DEV && !import.meta.env.VITEST && !import.meta.env.VITE_DISABLE_ICON_WARNINGS) {
    if ([__props.icon, __props.svg].filter(Boolean).length > 1)
      console.error('Pass only one of `icon` or `svg` to GIcon', el)

    if (__props.color)
      console.warn(`Please avoid using \`color\`, instead use \`class="text-${__props.color}"\``, el)

    if (__props.size?.includes('px') && sizeName.value)
      console.warn('Please migrate away from px sizes, use sm, md, lg', el)
  }
})
</script>

<template>
  <div
    v-if="icon && gradientColorName"
    class="g-icon g-icon-gradient-mask"
    v-bind="attrs"
    :style="[
      {
        '--mask-image-url': gradientMaskUrl,
        '--mask-gradient-color': gradientColorName
      }
    ]"
  />
  <FontAwesomeIcon
    v-else-if="icon"
    :icon="icon"
    class="g-icon"
    v-bind="attrs"
  />
  <svg
    v-else-if="parsedSvg"
    aria-hidden="true"
    class="g-icon"
    :viewBox="parsedSvg.viewBox"
    :preserveAspectRatio="parsedSvg.preserveAspectRatio"
    v-bind="attrs"
  >
    <title v-if="$attrs.title">{{ $attrs.title }}</title>
    <g v-bind="parsedSvg.groupAttrs" v-html="parsedSvg.inner"/>
  </svg>
</template>

<style lang="scss">
.g-icon {
  fill: currentcolor;
  max-width: initial;
  stroke: currentcolor;

  &.no-stroke {
    stroke: none;
  }
}

.g-icon-gradient-mask {
  background: var(--mask-gradient-color);
  display: inline-block; /* Ensure it behaves like an icon in layout */

  /* Apply the encoded SVG as the mask */
  mask-image: var(--mask-image-url);
  mask-position: center;
  mask-repeat: no-repeat;

  /* Size and position the mask to fit the bounds of the div */
  mask-size: contain;
}

.g-icon.g-illustration {
  fill: initial;
  stroke: initial;
}
</style>
